/**
 * All user-facing Turkish text of the app (spec §3.5). Code stays English; only values are Turkish.
 *
 * - Placeholders use `{name}` syntax and are filled by `t(key, params)` (see `./index.ts`).
 * - Write strings in natural case; use `upperTr()` for all-caps display so `i` → `İ` works.
 *   (A few comic headlines are already written in caps because that is how they are designed.)
 */
import type { AwardId, EngineErrorCode, GamePhase, Role, TimerKey } from '@20fight/engine';
import type { KickReason, ServerErrorCode } from '@20fight/server/protocol';
import type { AppErrorCode, ClientErrorCode, RestErrorCode } from '@/net/errors';

type Phase = 'lobby' | GamePhase;
type CustomCategoryIssueCode =
  | 'NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'EMOJI_REQUIRED'
  | 'EMOJI_INVALID'
  | 'TOO_FEW_ITEMS'
  | 'TOO_MANY_ITEMS'
  | 'TEXT_TOO_LONG'
  | 'ITEM_NAME_REQUIRED'
  | 'ITEM_NAME_TOO_LONG'
  | 'ITEM_EMOJI_INVALID'
  | 'INVALID_TIER'
  | 'DUPLICATE_ITEM';

/** Engine error messages. Typed as a full record so a new engine code fails the typecheck. */
const engineErrors: Record<EngineErrorCode, string> = {
  INVALID_INTENT: 'Bu hamle geçersiz.',
  INVALID_PROFILE: 'Kullanıcı adı ya da avatar geçersiz.',
  NOT_A_MEMBER: 'Bu odanın üyesi değilsin.',
  NOT_HOST: 'Bunu sadece oda sahibi yapabilir.',
  BANNED: 'Bu odadan yasaklandın.',
  ROOM_FULL: 'Oda dolu, yer kalmadı.',
  WRONG_PASSWORD: 'Şifre yanlış.',
  APPROVAL_REQUIRED: 'Bu odaya girmek için oda sahibinin onayı gerekiyor.',
  ALREADY_MEMBER: 'Zaten bu odadasın.',
  NOT_PENDING: 'Bekleyen böyle bir istek yok.',
  TOO_MANY_REQUESTS: 'Çok fazla bekleyen istek var, biraz sonra tekrar dene.',
  CANNOT_KICK_SELF: 'Kendini odadan atamazsın.',
  GAME_IN_PROGRESS: 'Oyun devam ediyor, bunu şimdi yapamazsın.',
  TEAMS_FULL: 'Takımlar dolu.',
  JURY_FULL: 'Tarafsız jüri dolu (en fazla 4 kişi).',
  INVALID_NAME: 'Bu isim geçersiz.',
  INVALID_ORDER: 'Oyuncu sırası geçersiz.',
  INVALID_SETTINGS: 'Ayarlar geçersiz.',
  PASSWORD_REQUIRED: 'Şifreli oda için bir şifre belirlemelisin.',
  TEAM_TOO_SMALL: 'Her takımda en az 2 oyuncu olmalı.',
  TOO_FEW_PLAYERS: 'Takımlarda toplam en az 4 oyuncu olmalı.',
  TOO_MANY_PLAYERS: 'Takımlarda toplam en fazla 12 oyuncu olabilir.',
  TOO_MANY_JURORS: 'En fazla 4 tarafsız jüri olabilir.',
  UNASSIGNED_PLAYERS:
    'Atanmamış oyuncular var: herkesi bir takıma ya da jüriye ata (ya da odadan çıkar).',
  EMPTY_CATEGORY_POOL: 'Kategori havuzu boş: en az bir kategori seç.',
  CATEGORY_NOT_FOUND: 'Kategori bulunamadı.',
  NOT_ENOUGH_ITEMS: 'Seçilen kategorilerde bu kadar tur için yeterli ürün yok.',
  NO_GAME: 'Şu an oynanan bir oyun yok.',
  WRONG_PHASE: 'Bunun için doğru an değil.',
  NOT_A_JUROR: 'Bu turda jüri değilsin.',
  WRONG_ITEM: 'Bu ürün şu an puanlanmıyor.',
  ALREADY_VOTED: 'Bu ürüne zaten puan verdin.',
  INVALID_SCORE: 'Puan 1 ile 10 arasında, 0,5 adımlarla olmalı.',
  NOT_YOUR_TURN: 'Sıra sende değil.',
  ITEM_NOT_AVAILABLE: 'Bu ürün artık vitrinde değil.',
  BID_TOO_LOW: 'Teklif çok düşük.',
  BID_TOO_HIGH: 'O kadar puanın yok.',
  CANNOT_PASS_OPENING: 'Açan oyuncu pas geçemez, bir teklif vermelisin.',
  NOT_GAME_OVER: 'Oyun henüz bitmedi.',
};

const serverErrors: Record<ServerErrorCode, string> = {
  NOT_FOUND: 'Bulunamadı. Oda kapanmış ya da kod yanlış olabilir.',
  BAD_TOKEN: 'Kimliğin doğrulanamadı. Sayfayı yenileyip tekrar dene.',
  PENDING_APPROVAL: 'İsteğin oda sahibine iletildi, onay bekleniyor.',
  RATE_LIMITED: 'Biraz yavaş! Birkaç saniye sonra tekrar dene.',
  SHUTTING_DOWN: 'Sunucu bakıma giriyor, şu an yeni oyun başlatılamıyor.',
  INVALID_PAYLOAD: 'Geçersiz istek.',
  NOT_IN_ROOM: 'Bir odada değilsin.',
  ROOM_LIMIT: 'Sunucu dolu, şu an yeni oda açılamıyor. Biraz sonra tekrar dene.',
  ROOM_TAKEN: 'Bu oda adı şu an kullanımda. Başka bir ad seç ya da odaya katıl.',
  BOTS_DISABLED: 'Botlar bu sunucuda kapalı.',
  NOT_A_BOT: 'Bu oyuncu bir bot değil.',
  INTERNAL: 'Sunucuda bir hata oldu. Tekrar dene.',
};

const restErrors: Record<Exclude<RestErrorCode, ServerErrorCode>, string> = {
  BAD_REQUEST: 'Geçersiz istek.',
  INVALID_CATEGORY: 'Kategori geçersiz, alanları kontrol et.',
  PLAYER_ID_REQUIRED: 'Oyuncu kimliği eksik.',
  INVALID_PLAYER_ID: 'Oyuncu kimliği geçersiz.',
  DB_UNAVAILABLE:
    'Veritabanına şu an ulaşılamıyor. Oyun oynanabilir ama geçmiş ve özel kategoriler kapalı.',
  PAYLOAD_TOO_LARGE: 'Gönderilen veri çok büyük.',
  UNSUPPORTED_MEDIA_TYPE: 'Desteklenmeyen veri biçimi.',
  UNAUTHORIZED: 'Yetkin yok.',
};

const clientErrors: Record<ClientErrorCode, string> = {
  TIMEOUT: 'Sunucu cevap vermedi. Bağlantını kontrol edip tekrar dene.',
  NETWORK: 'Sunucuya ulaşılamadı. İnternet bağlantını kontrol et.',
  OFFLINE: 'Bağlantı yok.',
  UNKNOWN: 'Beklenmeyen bir hata oldu.',
};

const errors: Record<AppErrorCode, string> = {
  ...engineErrors,
  ...serverErrors,
  ...restErrors,
  ...clientErrors,
};

const customCategoryIssues: Record<CustomCategoryIssueCode, string> = {
  NAME_REQUIRED: 'Kategoriye bir isim ver.',
  NAME_TOO_LONG: 'Kategori adı en fazla 30 karakter olabilir.',
  EMOJI_REQUIRED: 'Bir emoji seç.',
  EMOJI_INVALID: 'Bu bir emoji değil.',
  TOO_FEW_ITEMS: 'En az 10 farklı ürün yazmalısın.',
  TOO_MANY_ITEMS: 'En fazla 200 ürün ekleyebilirsin.',
  TEXT_TOO_LONG: 'Liste çok uzun.',
  ITEM_NAME_REQUIRED: 'Boş ürün adı var.',
  ITEM_NAME_TOO_LONG: 'Ürün adı en fazla 40 karakter olabilir.',
  ITEM_EMOJI_INVALID: 'Ürünün başındaki emoji geçersiz.',
  INVALID_TIER: 'Seviye sadece |S, |A, |B ya da |C olabilir.',
  DUPLICATE_ITEM: 'Aynı ürün birden fazla kez yazılmış.',
};

const phases: Record<Phase, string> = {
  lobby: 'Lobi',
  'round-intro': 'Tur başlıyor',
  jury: 'Jüri puanlıyor',
  'auction-intro': 'Açık artırma başlıyor',
  auction: 'Açık artırma',
  'round-result': 'Tur sonucu',
  'game-over': 'Oyun bitti',
};

const roles: Record<Role, string> = {
  A: 'Takım A',
  B: 'Takım B',
  jury: 'Tarafsız Jüri',
  none: 'Atanmamış',
  spectator: 'İzleyici',
};

const timers: Record<TimerKey, string> = {
  roundIntro: 'Tur tanıtımı',
  juryItem: 'Jüri süresi',
  auctionIntro: 'Açık artırma tanıtımı',
  pick: 'Ürün seçme süresi',
  open: 'Açılış teklifi süresi',
  turn: 'Teklif süresi',
  broke: 'Karar süresi',
  sold: 'Satış arası',
  autoAssign: 'Otomatik atama',
};

const kicked: Record<KickReason, string> = {
  kicked: 'Oda sahibi seni odadan çıkardı.',
  banned: 'Bu odadan yasaklandın.',
  'other-tab': 'Oyun başka bir sekmede açıldı.',
  'room-closed': 'Oda kapandı.',
  shutdown: 'Sunucu bakıma girdi, oda kapandı.',
};

const awards: Record<AwardId, { name: string; description: string; value: string }> = {
  esnaf: {
    name: 'Kapalıçarşı Esnafı',
    description: 'Harcadığı puan başına en çok jüri puanı toplayan alıcı.',
    value: 'puan başına {value}',
  },
  mirasyedi: {
    name: 'Mirasyedi',
    description: 'Tek seferde en pahalı alımı yapan savurgan.',
    value: '{item} {value}',
  },
  cimri: {
    name: 'Cimri',
    description: 'Bir turda en çok puanı kasada bırakan.',
    value: '{value} puanı kasada bıraktı',
  },
  'sert-juri': {
    name: 'Sert Jüri',
    description: 'En düşük ortalama puanı veren jüri (en az 5 oy).',
    value: 'ort. {value}',
  },
  'yumusak-kalp': {
    name: 'Yumuşak Kalp',
    description: 'En yüksek ortalama puanı veren jüri (en az 5 oy).',
    value: 'ort. {value}',
  },
};

export const tr = {
  app: {
    name: '20fight!',
    tagline: '20 puanla açık artırma, kör jüri, takım kapışması!',
  },
  common: {
    ok: 'Tamam',
    cancel: 'Vazgeç',
    back: 'Geri',
    close: 'Kapat',
    retry: 'Tekrar dene',
    loading: 'Yükleniyor…',
    save: 'Kaydet',
    copy: 'Kopyala',
    copied: 'Kopyalandı!',
    yes: 'Evet',
    no: 'Hayır',
    you: 'sen',
    home: 'Ana sayfa',
    points: '{n} puan',
    seconds: '{n} sn',
    unknownPlayer: 'Bilinmeyen oyuncu',
  },
  titles: {
    home: 'Ana Sayfa',
    room: 'Oda {code}',
    lobby: 'Lobi · {code}',
    game: 'Tur {round} · {code}',
    results: 'Sonuçlar · {code}',
    leaderboard: 'Liderlik',
    pastGame: 'Geçmiş Oyun',
    notFound: 'Sayfa bulunamadı',
    kit: 'UI Kit',
  },
  connection: {
    idle: 'Bağlı değil',
    connecting: 'Bağlanıyor…',
    connected: 'Bağlandı',
    reconnecting: 'Bağlantı koptu, yeniden bağlanılıyor…',
    offline: 'Bağlantı yok. Sunucuya ulaşılamıyor.',
    retry: 'Yeniden bağlan',
  },
  profile: {
    title: 'Önce kendini tanıt!',
    nameLabel: 'Kullanıcı adın',
    namePlaceholder: 'Örn. Zeynep',
    avatarLabel: 'Avatarını seç',
    submit: 'DEVAM ET',
    edit: 'Profili düzenle',
    nameRequired: 'Bir kullanıcı adı yaz.',
    nameTooLong: 'Kullanıcı adı en fazla 20 karakter olabilir.',
    nameInvalid: 'Kullanıcı adında geçersiz karakterler var.',
    inviteHint: '{code} odasına davetlisin. Önce adını yaz, avatarını seç; sonra içeri gir.',
  },
  home: {
    title: 'Hoş geldin!',
    createRoom: 'ODA KUR',
    creating: 'Oda kuruluyor…',
    joinRoom: 'ODAYA KATIL',
    codeLabel: 'Oda kodu',
    codePlaceholder: 'KEBAP7',
    invalidCode: 'Oda kodu 3–6 karakter olmalı (harf ve rakam).',
    backToRoom: 'Odaya geri dön ({code})',
    leaderboardLink: 'Geçmiş & Liderlik',
    howTo: 'İki takım, 10 ürün, 20 puan. Jüri önce kör puanlar, alıcılar sonra kapışır!',
    profileCaption: 'SEN KİMSİN?',
    playCaption: 'HADİ KAPIŞ!',
    or: 'ya da',
    joinHint: 'Oda kodunu yaz ya da davet linkini yapıştır.',
    codeAria: 'Oda kodu (3–6 karakter)',
    nameFirst: 'Önce kullanıcı adını yaz.',
    codeIncomplete: 'Oda kodu en az 3 karakter olmalı.',
    roomNameLabel: 'Oda adı (isteğe bağlı)',
    roomNameAria: 'Oda adı (isteğe bağlı, 3–6 harf/rakam)',
    roomNamePlaceholder: 'OFIS',
    roomNameHint: 'Boş bırakırsan rastgele bir kod verilir.',
    roomNameTooShort: 'Oda adı en az 3 karakter olmalı.',
    listRoom: 'Odayı ana sayfadaki listede göster',
    rooms: {
      title: 'AÇIK ODALAR',
      aria: 'Açık odalar',
      loading: 'Odalar yükleniyor…',
      error: 'Oda listesi şu an alınamıyor.',
      empty: 'Şu an listede oda yok. İlk odayı sen kur!',
      players: '{n}/{max} oyuncu',
      spectators: '{n} izleyici',
      lobby: 'LOBİDE',
      playing: 'OYUNDA · TUR {round}/{rounds}',
      join: 'KATIL',
      watch: 'İZLE',
      password: 'Şifreli',
      approval: 'Onaylı',
      host: 'Kuran: {name}',
      enter: '{code} odasına gir',
    },
    howToOpen: 'NASIL OYNANIR?',
    soundLabel: 'Ses',
    rules: {
      title: 'NASIL OYNANIR?',
      teams: {
        title: 'İki takım, bir jüri',
        text: 'Mavi ve Kırmızı takım kapışır. Her turda her takımdan bir alıcı çıkar; geri kalan herkes o turun jürisidir.',
      },
      jury: {
        title: 'Jüri önce, kör puanlar',
        text: 'Vitrinde 10 ürün var. Jüri her ürüne 1–10 arası puan verir; kimse kimsenin puanını görmez.',
      },
      auction: {
        title: '20 puanla açık artırma',
        text: 'Her alıcının 20 puanı var ve tam 5 ürün almak zorunda. Açan en az 1 ile açar, sonra sırayla artır ya da PAS de.',
      },
      broke: {
        title: 'Meteliksiz kalırsan',
        text: 'Puanı biten alıcı teklif veremez: rakibi ürünü 1 puana alır ya da “Sen al” deyip bedavaya bırakır.',
      },
      score: {
        title: 'Puanı jüri belirler',
        text: 'Ürün satılınca jüri puanı açılır. Takımın puanı, aldığı ürünlerin jüri puanlarının toplamıdır. Ucuza iyi ürün = KELEPİR!',
      },
      win: {
        title: 'Kazanan',
        text: 'Turlar bitince toplamı yüksek takım kazanır. Eşitlikte artan puan, sonra en iyi ürün bakılır.',
      },
      close: 'ANLADIM, KAPIŞALIM!',
    },
  },
  room: {
    joining: 'Odaya giriliyor…',
    connecting: 'Sunucuya bağlanılıyor…',
    passwordTitle: 'Bu oda şifreli',
    passwordLabel: 'Oda şifresi',
    passwordHint: 'Oda sahibinin paylaştığı şifreyi yaz.',
    passwordSubmit: 'GİR',
    wrongPassword: 'Şifre yanlış, tekrar dene.',
    pendingTitle: 'Onay bekleniyor',
    pendingText: 'Oda sahibi seni içeri almayı düşünüyor… Bu sayfada kal.',
    cancelRequest: 'İsteği iptal et',
    rejectedTitle: 'İsteğin reddedildi',
    rejectedText: 'Oda sahibi seni bu odaya almadı.',
    otherTabTitle: 'Oyun başka bir sekmede açıldı',
    otherTabText:
      'Bu oda şu an başka bir sekmede ya da cihazda açık. Buradan devam etmek istersen diğeri kapanır.',
    continueHere: 'Burada devam et',
    notFoundTitle: 'Oda bulunamadı',
    notFoundText: '{code} kodlu bir oda yok. Oda kapanmış ya da kod yanlış olabilir.',
    errorTitle: 'Odaya girilemedi',
    leave: 'Odadan çık',
    leaveConfirm: 'Odadan çıkmak istediğine emin misin?',
    kicked,
  },
  lobby: {
    title: 'LOBİ',
    code: 'Oda kodu',
    copyLink: 'Davet linkini kopyala',
    linkCopied: 'Davet linki kopyalandı!',
    passwordProtected: 'Şifreli oda',
    start: 'OYUNU BAŞLAT ▶',
    starting: 'Başlatılıyor…',
    waitingHost: 'Oda sahibinin oyunu başlatması bekleniyor…',
    startBlocked: 'Başlatılamıyor: {reason}',
    teamA: 'Takım A',
    teamB: 'Takım B',
    joinTeam: 'BU TAKIMA GEÇ',
    becomeJury: 'JÜRİ OL',
    leaveRole: 'Kenara çekil',
    neutralJury: 'Tarafsız Jüri',
    neutralJuryHelp: 'Tarafsız jüri her turda puan verir ama hiç alıcı olmaz (en fazla 4 kişi).',
    unassigned: 'Atanmamışlar',
    unassignedHelp: 'Oyun başlamadan önce herkes bir takıma ya da jüriye atanmalı.',
    spectators: 'İzleyiciler',
    host: 'Oda sahibi',
    you: '(sen)',
    bot: 'BOT',
    disconnected: 'bağlantı yok',
    emptyTeam: 'Henüz kimse yok',
    playerCount: '{n} oyuncu',
    kick: 'Odadan at',
    ban: 'Yasakla',
    moveTo: 'Taşı: {role}',
    addBot: 'Bot ekle',
    addBotTo: 'Bot ekle ({role})',
    removeBot: 'Botu çıkar',
    joinRequests: 'Katılma istekleri',
    approve: 'Onayla',
    reject: 'Reddet',
    renameTeam: 'Takım adını değiştir',
    teamNameLabel: 'Takım adı',
    randomName: '🎲 Rastgele isim',
    dragHint: 'Sürükleyerek alıcı sırasını değiştir',
    roomLabel: 'ODA',
    inviteLink: 'DAVET LİNKİ',
    copyFailed: 'Kopyalanamadı. Link: {link}',
    hostWillStart: 'Oda sahibi başlatınca oyun başlayacak',
    joinModeBadge: 'Katılım: {mode}',
    playerMenu: '{name} için seçenekler',
    moveHeading: 'TAŞI',
    moveToNone: 'Kenara al',
    leaveTeam: 'KENARA ÇEKİL',
    banConfirm: '{name} yasaklansın mı? Bu odaya bir daha giremez.',
    kickConfirm: '{name} odadan atılsın mı?',
    orderHint: 'Sıra = alıcı sırası',
    randomOrderHint: 'Alıcı sırası oyun başında karıştırılacak',
    needPlayers: 'En az {n} oyuncu daha lazım',
    emptySeat: 'boş koltuk',
    juryCount: '{n}/{max}',
    juryFull: 'Jüri dolu (en fazla 4 kişi).',
    teamFull: 'Takımlar dolu (en fazla 12 oyuncu).',
    alreadyHere: 'Zaten buradasın.',
    noUnassigned: 'Herkes yerini aldı.',
    noRequests: 'Bekleyen istek yok.',
    spectatorsHelp: 'Oyun başladıktan sonra gelenler; bir sonraki rövanşta katılabilir.',
    botsDisabled: 'Bu sunucuda botlar kapalı.',
    addBotShort: '🤖 Bot',
    renamed: 'Takım adı güncellendi.',
    teamNamePlaceholder: 'Takım adı',
    teamNameInvalid: 'Takım adı 1–24 karakter olmalı.',
    blockers: {
      TEAM_TOO_SMALL: 'Her takımda en az 2 oyuncu olmalı (şu an {a} – {b}).',
      TOO_FEW_PLAYERS: 'Takımlarda toplam en az 4 oyuncu olmalı (şu an {total}).',
      TOO_MANY_PLAYERS: 'Takımlarda en fazla 12 oyuncu olabilir (şu an {total}).',
      TOO_MANY_JURORS: 'En fazla 4 tarafsız jüri olabilir (şu an {jury}).',
      UNASSIGNED_PLAYERS: 'Atanmamış {n} kişi var: takıma, jüriye ata ya da odadan çıkar.',
      EMPTY_CATEGORY_POOL: 'Kategori havuzu boş: rastgele turlar için en az bir kategori seç.',
      NOT_ENOUGH_ITEMS_FIXED:
        '“{name}” kategorisinde {rounds} tur için yeterli ürün yok ({need} gerekir, {have} var).',
      NOT_ENOUGH_ITEMS_POOL:
        'Havuzdaki kategorilerde {n} rastgele tur için yeterli ürün yok: havuza kategori ekle ya da tur sayısını azalt.',
    },
    randomTeamNames: [
      'Kebap Kartelleri',
      'Lahmacun Lordları',
      'Simit Sultanları',
      'Baklava Baronları',
      'Pazarlık Panterleri',
      'Çay Ocağı Çetesi',
      'Kumpir Kartalları',
      'Dürüm Dinamoları',
      'Mantı Mafyası',
      'Ayran Avcıları',
      'Börek Bombardımanı',
      'Kokoreç Kobraları',
      'Midye Müfrezesi',
      'Pide Piyadeleri',
      'Turşu Tayfası',
      'Künefe Komandoları',
    ],
  },
  settings: {
    title: '⚙️ AYARLAR',
    readOnly: 'Ayarları sadece oda sahibi değiştirebilir.',
    rounds: {
      label: 'Tur sayısı',
      auto: 'Otomatik',
      help: 'Otomatikte tur sayısı büyük takımın oyuncu sayısına eşit olur; böylece herkes en az bir kez alıcı olur.',
      autoHint: 'otomatik · takım boyu kadar',
      setAuto: 'Otomatiğe al',
    },
    readOnlyBadge: 'SADECE OKUMA',
    buyerOrder: {
      label: 'Alıcı sırası',
      options: { ordered: 'Sıralı', random: 'Rastgele' },
      help: 'Sıralı: takımdaki sıraya göre. Rastgele: oyun başında karıştırılır.',
    },
    itemOrder: {
      label: 'Ürün sırası',
      options: { 'opener-picks': 'Açan seçer', fixed: 'Sabit sıra', surprise: 'Sürpriz' },
      help: {
        'opener-picks': 'Sırası gelen alıcı vitrinden istediği ürünü masaya koyar.',
        fixed: 'Ürünler herkesin gördüğü karışık bir sırayla gelir.',
        surprise: 'Ürünler kapalı kartlarla gelir; alıcılar sıradakini masaya konunca görür.',
      },
    },
    roundCategories: {
      label: 'Tur kategorileri',
      round: '{n}. tur',
      random: 'Rastgele 🎲',
      help: 'Her tur için bir kategori seç ya da havuzdan rastgele gelsin.',
    },
    categoryPool: {
      label: 'Kategori havuzu',
      help: 'Rastgele turlarda bu kategorilerden çekilir.',
      selectAll: 'Hepsini seç',
      selectNone: 'Hiçbirini seçme',
      all: 'Hepsi',
      none: 'Hiçbiri',
      count: '{n}/{total} seçili',
      customTag: 'ÖZEL',
      empty: 'Havuz boş: rastgele turlar için en az bir kategori seç.',
      unknown: 'Bilinmeyen kategori',
    },
    categories: {
      label: 'Kategoriler',
    },
    pace: {
      label: 'Tempo',
      options: { normal: 'Normal', fast: 'Hızlı' },
      help: 'Hızlı tempoda bütün süreler kısalır.',
    },
    alcohol: {
      label: 'Alkollü içecekler',
      options: { on: 'Dahil', off: 'Hariç' },
      help: 'Kapalıyken alkollü ürünler hiç çekilmez.',
    },
    listed: {
      label: 'Oda listesinde',
      options: { on: 'Açık', off: 'Gizli' },
      help: 'Açıkken oda, ana sayfadaki AÇIK ODALAR listesinde görünür.',
    },
    joinMode: {
      label: 'Katılım',
      options: { open: 'Açık', password: 'Şifreli', approval: 'Onaylı' },
      help: {
        open: 'Kodu bilen herkes girebilir.',
        password: 'Girmek için şifre gerekir.',
        approval: 'Her katılma isteğini sen onaylarsın.',
      },
    },
    password: {
      label: 'Oda şifresi',
      placeholder: 'Şifre belirle',
      set: 'Şifre belirlendi',
      change: 'Yeni şifre (boş bırakırsan aynı kalır)',
      needed: 'Şifreli oda için bir şifre yaz.',
    },
    customCategory: {
      open: 'Özel +',
      title: 'Özel kategori',
      nameLabel: 'Kategori adı',
      emojiLabel: 'Emoji',
      itemsLabel: 'Ürünler (her satıra bir tane, en az 10)',
      itemsHelp: 'İsteğe bağlı: başa bir emoji, sona |S |A |B |C ile seviye. Örn: 🍕 Pizza|A',
      save: 'KAYDET',
      saving: 'Kaydediliyor…',
      created: '“{name}” kategorisi eklendi!',
      issues: customCategoryIssues,
      issueAtLine: '{line}. satır: {message}',
      emojiOther: 'Başka bir emoji:',
      emojiOtherPlaceholder: 'Yapıştır',
      namePlaceholder: 'Örn. Ofis Atıştırmalıkları',
      itemsPlaceholder: '🍕 Pizza|A\n🌯 Dürüm|S\nMercimek çorbası|B\n…',
      previewTitle: 'ÖNİZLEME',
      previewCount: '{n} ürün',
      previewNeed: '{n}/{min} · en az {min} ürün gerekli',
      previewReady: '{n} ürün hazır!',
      previewEmpty: 'Yazdığın ürünler burada görünecek.',
      duplicates: '{n} tekrar eden satır atlanacak: {names}',
      tiers: 'Seviyeler: S {S} · A {A} · B {B} · C {C} · seviyesiz {none}',
      tierHelpTitle: 'Seviye nedir?',
      tierHelp:
        'S en iyi, C en zayıf ürünler. Seviye verirsen her turun vitrini dengeli gelir (2 S, 3 A, 3 B, 2 C). Vermezsen ürünler rastgele çekilir.',
      dbDown: 'Veritabanı kapalı: özel kategoriler şu an kaydedilemiyor.',
      notHost: 'Özel kategorileri sadece oda sahibi ekleyebilir.',
    },
    savedCategories: {
      open: 'Kayıtlı kategoriler',
      title: 'Kayıtlı kategoriler',
      empty: 'Henüz kayıtlı özel kategori yok.',
      add: 'Havuza ekle',
      itemCount: '{n} ürün',
      unavailable: 'Kayıtlı kategoriler şu an yüklenemiyor.',
      inPool: 'Havuzda ✓',
      remove: 'Çıkar',
      added: '“{name}” havuza eklendi.',
    },
  },
  roles,
  phases,
  timers,
  game: {
    round: 'TUR {n}/{total}',
    category: 'Kategori',
    vs: 'VS',
    buyers: 'ALICILAR',
    jury: 'JÜRİ',
    spectating: 'İzleyicisin: bu oyunu seyrediyorsun, bir sonraki rövanşta katılabilirsin.',
    timeLeft: '⏱ {s}',
    teamTotals: '{a} – {b}',
    soundOn: 'Sesi aç',
    soundOff: 'Sesi kapat',
    title: '20FIGHT!',
    rulesButton: 'Kurallar',
    rulesTitle: 'NASIL OYNANIR?',
    rulesList: [
      'Her turda iki takımın birer alıcısı kapışır; geri kalan herkes jüridir.',
      'Önce jüri vitrindeki 10 ürünü tek tek, gizlice 1–10 arası puanlar. Puanlar satışa kadar saklı kalır.',
      'Sonra açık artırma: iki alıcının da 20 puanı var ve ikisi de tam 5 ürün almak zorunda.',
      'Açan en az 1 puanla açar, pas geçemez. Sonra sırayla ya artırırsın ya da PAS dersin; PAS gelince ürün en yüksek teklife satılır.',
      'Satılan ürünün jüri puanı hemen açıklanır: ucuza iyi ürün KELEPİR, pahalıya kötü ürün KAZIKLANDI!',
      'Puanı biten alıcı meteliksiz kalır: rakibi kalan ürünlere karar verir (Ben alırım = 1 puan, Sen al = bedava). İkisi de meteliksizse almak bedava.',
      'Takım puanı, alıcılarının aldığı ürünlerin jüri puanları toplamıdır. Artan puan sadece eşitliği bozar.',
    ],
    rulesKeys: 'Klavye: ↑/↓ teklifi değiştir · Enter teklif ver · P pas · jüride ←/→ ve Enter',
    juryOfRound: 'Bu turun jürisi',
    firstOpener: 'İlk açan: {name}',
    you: 'SEN',
    roleBuyer: 'Bu tur alıcısın!',
    roleBuyerHint: 'Jüri puanlarken vitrine bak, sonra 20 puanla 5 ürün kap.',
    roleBuyerOpensHint: 'İlk ürünü sen açacaksın. Jüri puanlarken vitrine bak!',
    roleJuror: 'Bu tur jüridesin',
    roleJurorHint: 'Ürünleri tek tek, gizlice 1–10 arası puanla.',
    roleSpectator: 'İzleyici',
  },
  jury: {
    header: 'JÜRİ PUANLIYOR',
    giveScore: 'PUANI VER!',
    yourScore: 'Puanın: {score}',
    voted: 'Puanın kaydedildi!',
    waitingOthers: 'Diğer jüri üyeleri bekleniyor…',
    buyerWaiting: 'Bu tur alıcısın: jüri ürünleri puanlarken bekle ve vitrine göz at!',
    progress: '{n}/{total}',
    votedCount: '{n} kişi oy verdi',
    hiddenItem: 'Sürpriz ürün',
    scoreHelp: '1 ile 10 arası puan ver (0,5 adımlarla).',
    waitingTitle: 'JÜRİ PUANLIYOR…',
    nowScoring: 'Şu an puanlanan',
    yourVotes: 'SENİN PUANLARIN',
    noVotesYet: 'Henüz puan vermedin.',
    buyersWaiting: 'Bekliyorlar, vitrine bakıyorlar.',
    vitrinHint: 'Vitrine göz at, stratejini kur!',
    surpriseHint: 'Sürpriz mod: ürünler kapalı! Masaya konunca açılacaklar.',
    spectatorWaiting: 'Jüri puanlarken vitrine göz at.',
    secret: 'Puanlar satışa kadar gizli!',
  },
  auction: {
    yourTurn: '{name}, sıra sende!',
    thinking: '{name} DÜŞÜNÜYOR…',
    picking: '{name} SEÇİYOR…',
    pickPrompt: 'Masaya bir ürün seç!',
    sold: 'SATILDI!',
    take: 'Ben alırım ({cost})',
    give: 'Sen al',
    pass: 'PAS',
    allIn: 'HEPSİ ({n})',
    bid: 'TEKLİF VER!',
    open: 'AÇ',
    raise: 'ARTIR',
    opener: 'AÇAN: {name}',
    highBid: 'EN YÜKSEK: {amount} · {name}',
    noBid: 'Henüz teklif yok',
    onTable: 'MASADA',
    vitrin: 'VİTRİN',
    queue: 'Sıradaki',
    hiddenCard: '?',
    insufficient: 'Yetersiz puan',
    broke: 'Meteliksiz',
    brokePrompt: '{name} meteliksiz! Bu ürünü sen mi alırsın, ona mı verirsin?',
    bought: 'ALDIKLARI {n}/5',
    pointsLeft: '💰 {n} / 20',
    autoAssign: 'Otomatik atanıyor…',
    soldTo: '{name} aldı · {price} puan',
    juryScore: 'Jüri puanı',
    bargain: 'KELEPİR',
    ripoff: 'KAZIKLANDI',
    noVotes: 'Oy yok (5,5)',
    bidLabel: 'Teklifin',
    reactions: 'Tepkiler',
    auctionStarts: 'AÇIK ARTIRMA BAŞLIYOR!',
    openerFirst: '{name} açıyor!',
    rulesShort: 'İkinize de 20 puan · ikiniz de tam 5 ürün alacaksınız',
    deciding: '{name} KARAR VERİYOR…',
    selling: 'SATIYORUM… SATIYORUM…',
    tableEmpty: 'Masaya ne gelecek?',
    openPrompt: 'Açılış teklifi: en az 1, PAS yok!',
    raisePrompt: 'Artır ya da PAS de!',
    brokeExplain: 'Rakibin meteliksiz! Kalan ürünlere sen karar veriyorsun.',
    bothBrokeExplain: 'İkiniz de meteliksiz! Almak bedava: ürünü ya sen al ya da rakibine ver.',
    brokeWatching: '{name} meteliksiz! Karar rakibinde.',
    bothBrokeWatching: 'İkisi de meteliksiz! Almak bedava.',
    brokeMode: 'METELİKSİZ MODU',
    bothBrokeMode: 'İKİSİ DE METELİKSİZ',
    autoBanner: 'Kalan ürünler {name} bedava!',
    tookIt: '{name} aldı!',
    gotFree: '{name} bedavaya aldı!',
    juryLabel: 'Jüri:',
    pricePoints: '{n} puan',
    insufficientPass: 'YETERSİZ PUAN → PAS',
    timeoutPass: 'SÜRE DOLDU → PAS',
    timeoutBid: 'SÜRE DOLDU → {n}',
    youInsufficient: 'Puanın yetmedi, otomatik PAS geçildi.',
    keysHintTurn: '↑/↓ teklif · Enter gönder · P pas',
    keysHintOpen: '↑/↓ teklif · Enter gönder',
    confirmAllIn: 'EMİN MİSİN? ({n})',
    surpriseTitle: 'SÜRPRİZ',
    hiddenLeft: '{n} kapalı kart',
    tookChoice: 'BEN ALIRIM!',
    gaveChoice: 'SEN AL!',
    spent: 'Harcadı: {n} puan',
  },
  roundResult: {
    title: 'TUR SONUCU',
    roundScore: 'Tur puanı',
    gameScore: 'Genel skor',
    leftover: 'Kalan puan: {n}',
    nextRound: 'SONRAKİ TUR ▶',
    finish: 'SONUÇLARA GEÇ ▶',
    waitingHost: 'Oda sahibi sonraki tura geçecek…',
    tabsLabel: 'Tur detayları',
    tabs: {
      purchases: 'BU TURUN ALIMLARI',
      juryVotes: 'JÜRİ OYLARI',
      rounds: 'TURLAR',
      players: 'OYUNCULAR (ŞU ANA KADAR)',
    },
    winner: '{team} TURU KAZANDI!',
    draw: 'TUR BERABERE!',
    wonShort: 'TURU KAZANDI!',
    drawShort: 'BERABERE!',
  },
  results: {
    title: 'OYUN BİTTİ',
    winner: '{team} KAZANDI!',
    draw: 'BERABERE!',
    decidedBy: {
      score: 'Puan farkıyla',
      leftover: 'Puanlar eşit! Eşitliği kalan puan bozdu: {a} – {b}.',
      'best-item': 'Puanlar ve kalan puan eşit! Eşitliği en iyi ürün bozdu: {a} – {b}.',
      draw: 'Puan, kalan puan, en iyi ürün… hepsi eşit. Dostluk kazandı!',
    },
    roundLine: 'Tur {n} ({category})',
    roundLineLabel: 'Tur skorları',
    tabsLabel: 'Oyun detayları',
    tabs: {
      purchases: 'ALIMLAR',
      juryVotes: 'JÜRİ OYLARI',
      rounds: 'TURLAR',
      players: 'OYUNCULAR',
    },
    purchases: {
      item: 'ÜRÜN',
      buyer: 'ALAN',
      price: 'FİYAT',
      meanA: 'MAVİ JÜRİ ORT.',
      meanB: 'KIRMIZI JÜRİ ORT.',
      meanJ: 'TARAFSIZ',
      score: 'SKOR',
      verdict: 'Karar',
      noVotes: 'oy yok',
      noVotesHelp: 'Kimse oy vermedi, puan 5,5 sayıldı.',
      noGroupVotes: 'Bu gruptan oy yok',
      empty: 'Bu turda satış olmadı.',
    },
    votes: {
      item: 'ÜRÜN',
      rowAvg: 'ORT.',
      score: 'SKOR',
      colAvg: 'ORTALAMA',
      missing: 'Oy yok',
      legend:
        '▲ ürüne verilen en yüksek oy · ▼ en düşük oy · SKOR: grup ortalamalarının ortalaması',
      groupA: 'Mavi',
      groupB: 'Kırmızı',
      groupJ: 'Tarafsız',
    },
    rounds: {
      heading: 'TUR {n}',
      leftover: 'Kalan: {n}',
      firstOpener: 'İlk açan',
      winner: 'Turu {team} aldı',
      draw: 'Tur berabere',
      bargains: '{n} kelepir',
      ripoffs: '{n} kazık',
      best: 'En iyi alım: {item} ({score})',
    },
    players: {
      player: 'OYUNCU',
      team: 'TAKIM / ROL',
      bought: 'ALDIĞI',
      spent: 'HARCADIĞI',
      scoreSum: 'ÜRÜN PUANI',
      votes: 'VERDİĞİ OY',
      avgGiven: 'ORT. VERDİĞİ',
      won: 'KAZANDI',
      jury: 'Tarafsız Jüri',
      bot: 'Bot',
      winYes: 'Kazandı',
    },
    columns: {
      round: 'Tur',
      item: 'Ürün',
      buyer: 'Alıcı',
      price: 'Fiyat',
      groupA: 'A',
      groupB: 'B',
      groupJ: 'J',
      score: 'Puan',
      verdict: 'Karar',
      player: 'Oyuncu',
      role: 'Rol',
      itemsBought: 'Aldığı',
      pointsSpent: 'Harcadığı',
      avgItemScore: 'Ort. ürün puanı',
      votesGiven: 'Verdiği oy',
      avgGivenScore: 'Ort. verdiği puan',
      category: 'Kategori',
    },
    awardsTitle: 'ÖDÜLLER',
    awardPrice: '{value} puan',
    awardRound: 'Tur {n}',
    rematch: 'RÖVANŞ (aynı takımlar)',
    shuffle: 'TAKIMLARI KARIŞTIR',
    home: 'ANA SAYFA',
    history: 'GEÇMİŞ & LİDERLİK',
    waitingHost: 'Oda sahibi rövanş başlatabilir.',
    actionsLabel: 'Sonraki adım',
    total: 'Toplam',
  },
  awards,
  leaderboard: {
    title: 'LİDERLİK',
    recentGames: 'SON OYUNLAR',
    columns: {
      rank: '#',
      player: 'Oyuncu',
      games: 'Oyun',
      wins: 'Galibiyet',
      winRate: 'Kazanma %',
      avgItemScore: 'Ort. ürün puanı',
      avgGivenScore: 'Ort. verdiği puan',
    },
    empty: 'Henüz kimse oynamadı. İlk şampiyon sen ol!',
    noGames: 'Henüz bitmiş oyun yok.',
    gameLine: '{a} {scoreA} – {scoreB} {b}',
    players: '{n} oyuncu',
    view: 'Detay',
    subtitle: 'Kim kelepirci, kim mirasyedi? Bütün odaların karnesi burada.',
    topPlayers: 'EN İYİ OYUNCULAR',
    back: '← Ana sayfa',
    unavailableTitle: 'Arşiv kapalı',
    unavailableText:
      'Veritabanına şu an ulaşılamıyor. Oyunlar oynanabilir ama geçmiş ve liderlik tablosu kapalı.',
    winner: 'KAZANDI',
    draw: 'BERABERE',
    playCta: 'ODA KUR, İLK OYUNU OYNA',
    room: 'Oda {code}',
  },
  pastGame: {
    title: 'GEÇMİŞ OYUN',
    notFound: 'Oyun bulunamadı',
    notFoundHelp: 'Bu bağlantıdaki oyun kaydedilmemiş ya da silinmiş olabilir.',
    dbDown: 'Geçmiş oyunlar şu an açılamıyor',
    dbDownHelp: 'Veritabanına ulaşılamıyor. Biraz sonra tekrar dene.',
    failed: 'Oyun yüklenemedi',
    back: 'Liderlik',
    playedAt: '{date} tarihinde oynandı',
    room: 'Oda {code}',
    incomplete: 'Bu oyun yarıda kaldı; sadece oynanan turlar görünüyor.',
  },
  reactions: {
    label: 'Tepki gönder',
  },
  toast: {
    joined: '{name} odaya katıldı',
    left: '{name} odadan ayrıldı',
    hostChanged: 'Yeni oda sahibi: {name}',
    youAreHost: 'Oda sahibi artık sensin! Ayarları değiştirebilir ve oyunu başlatabilirsin.',
  },
  errorBoundary: {
    title: 'Bir şeyler ters gitti',
    text: 'Beklenmedik bir hata oldu. Sayfayı yenileyip tekrar dene.',
    reload: 'SAYFAYI YENİLE',
    home: 'Ana sayfaya dön',
  },
  notFound: {
    title: '404 · Burada kimse yok!',
    text: 'Aradığın sayfa ya kaçtı ya da hiç var olmadı.',
    home: 'ANA SAYFAYA DÖN',
  },
  errors,
} as const;

export type Dictionary = typeof tr;
